import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  TextField, MenuItem, Alert, Stack,
} from '@mui/material';
import { deletePatient } from '../api/patientApi';

const REASONS = ['Duplicate record', 'Test record', 'Entered by mistake', 'Other'];

/**
 * Confirms and performs a patient delete.
 *
 * The delete is a soft delete and is audited on the server (who, when, a
 * snapshot of the patient, and the reason chosen here), so if a record is
 * later reported missing, the audit log shows who removed it and why.
 *
 * @param patient   { id, name, mobileNo } or null (closed)
 * @param onClose   called on cancel
 * @param onDeleted called with the patient after a successful delete
 */
const DeletePatientDialog = ({ patient, onClose, onDeleted }) => {
  const open = Boolean(patient);
  const [reason, setReason] = useState(REASONS[0]);
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Fresh form every time the dialog opens for a patient.
  useEffect(() => {
    if (open) {
      setReason(REASONS[0]);
      setDetails('');
      setError('');
      setBusy(false);
    }
  }, [open, patient?.id]);

  const detailsRequired = reason === 'Other';
  const canSubmit = !busy && (!detailsRequired || details.trim().length > 0);

  const handleDelete = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    const fullReason = details.trim() ? `${reason}: ${details.trim()}` : reason;
    try {
      const res = await deletePatient(patient.id, fullReason);
      if (res.data?.succeeded) {
        onDeleted?.(patient);
      } else {
        setError(res.data?.messages?.[0] || 'Could not delete this patient.');
        setBusy(false);
      }
    } catch (e) {
      const status = e?.response?.status;
      setError(status === 403
        ? 'You do not have permission to delete patients.'
        : 'Could not delete this patient. Please try again.');
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete patient?</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2">
            <strong>{patient?.name || 'This patient'}</strong>
            {` (ID ${patient?.id}${patient?.mobileNo ? `, ${patient.mobileNo}` : ''})`} will be
            removed from the patient list and search. Their assessment history is kept.
          </Typography>

          <TextField
            select
            size="small"
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
          >
            {REASONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </TextField>

          <TextField
            size="small"
            label={detailsRequired ? 'Details (required)' : 'Details (optional)'}
            placeholder={reason === 'Duplicate record' ? 'e.g. duplicate of patient 2962' : ''}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            inputProps={{ maxLength: 400 }}
            multiline
            minRows={2}
            disabled={busy}
          />

          <Typography variant="caption" color="text.secondary">
            This is recorded in the audit log with your name and the reason.
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" color="error" onClick={handleDelete} disabled={!canSubmit}>
          {busy ? 'Deleting…' : 'Delete patient'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeletePatientDialog;
